import { apiService, type ApiResponse } from '@/services/apiService';

export type GameProviderHealthStatus = 'healthy' | 'out_of_sync' | 'down_risk' | 'na';

export interface GameProviderHealthItem {
  id: string;
  code: string;
  healthStatus: GameProviderHealthStatus;
  depositsOpen: boolean;
}

export interface GameProviderHealthResponse {
  items: GameProviderHealthItem[];
  updatedAt: string | null;
}

const HEALTH_CACHE_TTL_MS = 60 * 1000;

let healthCache: ApiResponse<GameProviderHealthResponse> | null = null;
let healthCacheTimestamp = 0;

export function isProviderDepositLocked(provider: { depositsOpen?: boolean }): boolean {
  return provider.depositsOpen === false;
}

export function applyProviderHealth<T extends { id: string }>(
  providers: T[],
  health: GameProviderHealthResponse | null | undefined
): T[] {
  if (!health || !Array.isArray(health.items)) {
    return providers;
  }
  const byId = new Map(health.items.map((item) => [item.id, item]));
  return providers.map((provider) => {
    const item = byId.get(provider.id);
    if (!item) {
      return provider;
    }
    return {
      ...provider,
      healthStatus: item.healthStatus,
      depositsOpen: item.depositsOpen,
    };
  });
}

/** Apply lock only when the health API succeeds. Fail-open otherwise. */
export async function mergeProvidersWithHealth<T extends { id: string }>(providers: T[]): Promise<T[]> {
  const health = await getGameProviderHealth();
  if (!health.success || !health.data) {
    return providers;
  }
  return applyProviderHealth(providers, health.data);
}

/**
 * Game management wallets nest provider id under `game.id`.
 * Same fail-open rule as catalog cards: lock PLAY only when health succeeds.
 */
export async function mergeGameWalletsWithHealth<T extends { game?: { id?: string } }>(
  wallets: T[]
): Promise<Array<T & { depositsOpen?: boolean; healthStatus?: GameProviderHealthStatus }>> {
  return mergeProvidersWithHealth(
    wallets.map((wallet, index) => ({
      ...wallet,
      id: wallet.game?.id ? String(wallet.game.id) : `unkeyed-${index}`,
    }))
  );
}

function emptyHealthResponse(): ApiResponse<GameProviderHealthResponse> {
  return {
    success: false,
    message: 'Failed to fetch game provider health',
    statusCode: 500,
    data: { items: [], updatedAt: null },
  };
}

/**
 * GET /game-providers/health — apply lock only when this call succeeds.
 * Short in-memory cache; Redis on the server is the source of truth.
 */
export async function getGameProviderHealth(
  forceRefresh = false
): Promise<ApiResponse<GameProviderHealthResponse>> {
  if (!forceRefresh && healthCache && Date.now() - healthCacheTimestamp < HEALTH_CACHE_TTL_MS) {
    return healthCache;
  }

  try {
    const response = await apiService.get<any>('/game-providers/health', false);
    const rawItems: any[] = Array.isArray(response?.data)
      ? response.data
      : (response?.data?.items || []);

    if (!response?.success || !Array.isArray(rawItems)) {
      return emptyHealthResponse();
    }

    const items: GameProviderHealthItem[] = rawItems
      .filter((item) => item && item.id)
      .map((item) => ({
        id: String(item.id),
        code: String(item.code || ''),
        healthStatus: item.healthStatus,
        depositsOpen: item.depositsOpen !== false,
      }));

    const result: ApiResponse<GameProviderHealthResponse> = {
      success: true,
      message: 'OK',
      statusCode: 200,
      data: {
        items,
        updatedAt: response.data?.updatedAt || null,
      },
    };
    healthCache = result;
    healthCacheTimestamp = Date.now();
    return result;
  } catch (error: any) {
    console.error('[GameProviderHealth] API error:', error?.message || error);
    return {
      ...emptyHealthResponse(),
      message: error?.message || 'Failed to fetch game provider health',
      statusCode: error?.statusCode || 500,
    };
  }
}

export function clearGameProviderHealthCache(): void {
  healthCache = null;
  healthCacheTimestamp = 0;
}
